import { __read } from "tslib";
import { logRequestEvent } from '@office-start/instrumentation-service';
import { logDiagnostic, logRequest } from '@office-start/telemetry-service';
import { getParamFromWindow } from '@office-start/window-utility';
import { applyHeaderFromTokenResponse } from './utilities/applyHeaderFromTokenResponse';
import { getHeaderValue } from './utilities/getHeaderValue';
import { getRandomRetryInterval } from './utilities/getRandomRetryInterval';
/*
 * Update request header and body with auth info
 */
function updateRequestConfig(requestConfig, tokenResponse, rpsHeaderPrefix, updateRequestBody) {
    if (!requestConfig) {
        return;
    }
    requestConfig.headers = applyHeaderFromTokenResponse(tokenResponse, requestConfig.headers, rpsHeaderPrefix);
    if (updateRequestBody) {
        updateRequestBody(requestConfig, tokenResponse);
    }
}
// eslint-disable-next-line max-params
export function requestWithAuthAndRetry(httpRequest, eventName, otelEventName, requestConfig, jwtAuthEnabled, getAccessToken, tokenConsumer, mockParam, resourceUrl, rpsHeaderPrefix, additionalAuthErrors, isRetriableError, updateRequestWithAuth, fromCache, silent, capDialogData, scope) {
    if (fromCache === void 0) { fromCache = false; }
    return new Promise(function (resolve, reject) {
        var tokenType = tokenConsumer || 'None';
        var noAuth = tokenType === 'None' ||
            (tokenType === 'OfficeHome' && !jwtAuthEnabled);
        var authErrorStatuses = additionalAuthErrors || new Set();
        authErrorStatuses.add(401);
        var finalErrorHandler = function (response) {
            var status = response.status;
            var customErrorCode = Number.NaN;
            if (!noAuth && authErrorStatuses.has(status)) {
                customErrorCode = 701 /* HttpStatusCode.TokenRefreshFailed */;
            }
            else if (isRetriableError && isRetriableError(status)) {
                customErrorCode = 703 /* HttpStatusCode.RetryFailed */;
            }
            if (customErrorCode !== Number.NaN) {
                logRequestEvent(eventName, customErrorCode, -1, '', fromCache);
                // TODO: Remove above aria logging after OTEL migration
                logRequest({
                    eventName: otelEventName,
                    fromCache: fromCache,
                    httpCode: customErrorCode,
                    latency: -1,
                    error: '',
                });
            }
            reject({
                httpResponse: response,
            });
        };
        // TODO: Honor RetryAfter header rather than using random delay
        var retry = function (config, response) {
            // retry request after delay
            var responseHeaders = response.headers;
            var retryAfter = getHeaderValue(responseHeaders, 'retry-after');
            var retryAfterMilliSeconds = getRandomRetryInterval();
            if (retryAfter) {
                retryAfterMilliSeconds = Math.ceil(parseFloat(retryAfter || '0') * 1000);
            }
            setTimeout(function () {
                var retryPromise = httpRequest(config);
                retryPromise.then(resolve, finalErrorHandler);
            }, retryAfterMilliSeconds);
        };
        if (mockParam) {
            var queryParamValue = getParamFromWindow(mockParam);
            if (queryParamValue) {
                if (!requestConfig.headers) {
                    requestConfig.headers = {};
                }
                requestConfig.headers["ohp-mock" /* OfficeHeader.Mock */] = queryParamValue;
            }
        }
        var tokenPromise = noAuth
            ? Promise.resolve(undefined)
            : getAccessToken(tokenType, false /* force fetch */, resourceUrl, scope, undefined, undefined, silent, capDialogData);
        tokenPromise.then(function (tokenResponse) {
            if (!noAuth) {
                if (!tokenResponse || !tokenResponse.success) {
                    // we were unable to fetch access token; log custom error and call error handler
                    var errorMsg = tokenResponse
                        ? tokenResponse.error
                        : '';
                    logRequestEvent(eventName, 702 /* HttpStatusCode.TokenUnavailable */, -1, typeof errorMsg === 'string' ? errorMsg : '', fromCache);
                    // TODO: Remove above aria logging after OTEL migration
                    logRequest({
                        eventName: otelEventName,
                        fromCache: fromCache,
                        httpCode: 702 /* HttpStatusCode.TokenUnavailable */,
                        latency: -1,
                        error: typeof errorMsg === 'string' ? errorMsg : '',
                    });
                    reject({
                        httpResponse: {
                            data: errorMsg === 'CAP_Error' ? errorMsg : null,
                            status: 702 /* HttpStatusCode.TokenUnavailable */,
                            headers: {},
                            ok: false,
                            statusText: 'Failed to fetch access token',
                            fromCache: false,
                        },
                    });
                    return;
                }
                // we've obtained an access token, either from a fetch or from the cache
                updateRequestConfig(requestConfig, tokenResponse, rpsHeaderPrefix, updateRequestWithAuth);
            }
            var instrumentedPromise = httpRequest(requestConfig);
            instrumentedPromise.then(resolve, function (response) {
                if (!noAuth && authErrorStatuses.has(response.status)) {
                    var claimsChallenge = getClaimsChallenge(response.headers);
                    // attempt to fetch new access token if unauthorized status code, then retry request if successful
                    getAccessToken(tokenType, true, resourceUrl, undefined /* scope */, false /* isBoot */, undefined /* claims */, undefined /* silent */, undefined /* capDialogData */, claimsChallenge).then(function (freshToken) {
                        if (!freshToken.success) {
                            finalErrorHandler(response);
                            return;
                        }
                        updateRequestConfig(requestConfig, freshToken, rpsHeaderPrefix, updateRequestWithAuth);
                        var retryPromise = httpRequest(requestConfig);
                        retryPromise.then(resolve, finalErrorHandler);
                    });
                }
                else if (isRetriableError &&
                    isRetriableError(response.status)) {
                    retry(requestConfig, response);
                }
                else {
                    reject({ httpResponse: response });
                }
            });
        });
    });
}
function getClaimsChallenge(responseHeaders) {
    var _a;
    var _b;
    var wwwAuthHeader = (_b = Object.entries(responseHeaders).find(function (_a) {
        var _b = __read(_a, 1), key = _b[0];
        return key.toLowerCase() === 'www-authenticate';
    })) === null || _b === void 0 ? void 0 : _b[1];
    if (wwwAuthHeader === null || wwwAuthHeader === void 0 ? void 0 : wwwAuthHeader.includes('insufficient_claims')) {
        var claimsMatch = wwwAuthHeader.match(/claims=\"(\S+)\"/);
        if (claimsMatch) {
            return claimsMatch[1];
        }
        logDiagnostic({
            eventName: 'Extract_ClaimsChallenge_Error',
            params: (_a = {},
                _a['Error'] = 'Failed to extract insufficient claims value',
                _a),
        });
    }
    return '';
}
//# sourceMappingURL=requestWithAuthAndRetry.js.map